export const STATUS = {
  CASE_STUDY: "CASE STUDY",
  CONCEPT: "CONCEPT / SELF-INITIATED",
  EXPERIMENT: "EXPERIMENT / R&D",
};

export const PROJECTS = [
  {
    slug: "aura-spatial",
    title: "AURA SPATIAL",
    tag: "WebGL / 3D Configurator",
    capability: "Digital Experiences",
    capabilitySlug: "digital-experiences",
    oneLiner: "A configurator where the product is explored, not selected from a list.",
    status: STATUS.CASE_STUDY,
    liveUrl: null,
    year: "2026",
    image:
      "https://images.unsplash.com/photo-1671519821564-ced7e41ee7ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMDNEJTIwZmx1aWQlMjBkYXJrJTIwbmVvbiUyMGx1eHVyeSUyMGFyY2hpdGVjdHVyZSUyMGdlb21ldHJpY3xlbnwwfHx8fDE3ODg0MjAwMTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "AURA SPATIAL — immersive WebGL 3D configurator, digital experience design by Elentara",
    seoTitle: "AURA SPATIAL — WebGL 3D Configurator | Elentara Case Study",
    seoDescription:
      "AURA SPATIAL by Elentara: a real-time WebGL 3D product configurator — interactive 3D experience design that turns a sales tool into an immersive brand experience.",
    role: "Digital Experience Design, WebGL Engineering",
    intro:
      "A real-time WebGL 3D product configurator that lets a visitor shape a vehicle in photorealistic detail — before it physically exists.",
    sections: {
      challenge:
        "Configurators are usually forms wearing a 3D costume: a viewport, a swatch row, a price. The brief was to remove that framing entirely and build something closer to standing beside the object in a controlled room, where changing a material feels like a decision rather than a click.",
      idea:
        "Treat the configurator as a lighting stage instead of an interface. The vehicle sits in a dark studio; the UI dissolves into the scene as the object rotates and only re-forms when the visitor comes to rest. Every choice is expressed as light behaviour first and a label second.",
      experience:
        "The visitor orbits a single object with inertial drag, cursor-proximity parallax and momentum that settles rather than snaps. Material changes cross-fade over eight hundred milliseconds so surfaces appear to cure rather than swap. On touch devices the orbit becomes a one-finger drag with a two-finger dolly, and the panel collapses into a bottom sheet.",
      designDirection:
        "Obsidian environment, one dominant key light, and a single accent hue per material family. Typography is reduced to mono labels and thin rules so nothing competes with the reflections. Negative space carries the premium signal — the object is never centred, it is composed.",
      technology: [
        "Three.js / WebGL2 with a custom PBR material pipeline",
        "HDR lighting stages with pre-filtered environment maps",
        "Draco-compressed geometry with progressive LOD loading",
        "GPU-instanced trim variants to avoid re-uploads on every change",
      ],
      process: [
        "Material and lighting studies rendered offline to lock the target look",
        "Real-time approximation pass — matching the offline render inside a frame budget",
        "Interaction prototyping for orbit, settle and material cross-fade timing",
        "Device tiering with resolution scaling and a static fallback frame",
      ],
      outcome:
        "The result is a configurator that behaves like an object rather than a form: it holds attention through material and light instead of options, and it holds sixty frames per second while doing it. Performance was treated as art direction — the frame budget shaped the lighting model, not the other way around.",
    },
    deliverables: [
      "Real-time WebGL configurator",
      "3D experience design",
      "PBR material pipeline",
      "Interaction design",
    ],
  },
  {
    slug: "neo-hyperion",
    title: "NEO HYPERION",
    tag: "Generative Soundscape",
    capability: "Creative Technology",
    capabilitySlug: "creative-technology",
    oneLiner: "Live audio becomes topology, light and weather in real time.",
    status: STATUS.CASE_STUDY,
    liveUrl: null,
    year: "2025",
    image:
      "https://images.unsplash.com/photo-1658660854207-8886b1d69bb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMDNEJTIwZmx1aWQlMjBkYXJrJTIwbmVvbiUyMGx1eHVyeSUyMGFyY2hpdGVjdHVyZSUyMGdlb21ldHJpY3xlbnwwfHx8fDE3ODg0MjAwMTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "NEO HYPERION — generative soundscape, audio-reactive creative technology by Elentara",
    seoTitle: "NEO HYPERION — Generative Soundscape | Elentara Case Study",
    seoDescription:
      "NEO HYPERION by Elentara: a generative soundscape where audio-reactive shaders turn live sound into an interactive visual instrument — creative technology as instrument.",
    role: "Creative Technology, Audio-Reactive Visuals",
    intro:
      "A generative soundscape where live audio drives a breathing visual instrument — every performance renders a world that will never repeat.",
    sections: {
      challenge:
        "Audio visualisation collapses into cliché the moment it becomes literal. Bars, rings and pulses read as decoration. The problem was to make sound legible as an environment — something that has terrain and weather rather than a waveform.",
      idea:
        "Decompose the signal into frequency bands and assign each band a physical consequence instead of a graphic. Bass displaces geometry, mid-range drives light temperature, transients trigger fracture, and silence is not empty — it becomes fog, drift and cooling.",
      experience:
        "The visitor steers a camera path through terrain that is being generated by whatever is playing. Because the mapping is physical rather than decorative, the same track twice never produces the same route: the system remembers energy over time, so the world carries the history of the performance.",
      designDirection:
        "Almost monochrome. Colour is earned by intensity, never applied for variety, so a loud passage is the only thing in the frame that is allowed to be bright. Motion easing is deliberately slow against fast audio to keep the piece cinematic instead of frantic.",
      technology: [
        "Web Audio API with FFT band decomposition and temporal smoothing",
        "Custom GLSL vertex displacement and emissive shading",
        "GPU particle systems driven by transient detection",
        "Deterministic seeding so a session can be replayed exactly",
      ],
      process: [
        "Signal studies — mapping frequency bands to physical consequences",
        "Shader prototyping against recorded reference tracks",
        "Latency tuning so visual response reads as instantaneous",
        "Fallback mode for devices without audio permission or WebGL2",
      ],
      outcome:
        "A system that behaves like an instrument rather than a plugin: it can be performed, it rewards restraint in the source material, and it degrades into a still, atmospheric state instead of breaking when the audio stops.",
    },
    deliverables: [
      "Generative audio engine",
      "Audio-reactive shaders",
      "Live visuals system",
      "Interactive web experience",
    ],
  },
  {
    slug: "chrono-monolith",
    title: "CHRONO MONOLITH",
    tag: "Interactive Sculpture",
    capability: "3D Design & Motion",
    capabilitySlug: "3d-design-motion",
    oneLiner: "A monolith that answers movement with light at architectural scale.",
    status: STATUS.CASE_STUDY,
    liveUrl: null,
    year: "2025",
    image:
      "https://images.unsplash.com/photo-1624139283078-74a0492f2ee3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMDNEJTIwZmx1aWQlMjBkYXJrJTIwbmVvbiUyMGx1eHVyeSUyMGFyY2hpdGVjdHVyZSUyMGdlb21ldHJpY3xlbnwwfHx8fDE3ODg0MjAwMTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "CHRONO MONOLITH — interactive 3D sculpture and experiential installation by Elentara",
    seoTitle: "CHRONO MONOLITH — Interactive Sculpture | Elentara Case Study",
    seoDescription:
      "CHRONO MONOLITH by Elentara: a motion-tracked interactive sculpture rendered across an LED volume — experiential design merging physical space with real-time 3D.",
    role: "Experiential Design, Installation Engineering",
    intro:
      "A twelve-metre interactive sculpture that tracks bodies in the room and answers movement with monolithic light.",
    sections: {
      challenge:
        "Large LED volumes usually play a loop. A loop cannot acknowledge you. The task was to make a room-scale object that appears to notice presence without resorting to gimmicks like mirrored silhouettes or floating name tags.",
      idea:
        "Give the monolith a single behaviour — attention. It leans, fractures and reassembles toward the boldest gesture in the room, and ignores small movement entirely. Restraint is what makes the response feel intentional rather than reactive.",
      experience:
        "Depth cameras resolve bodies into a coarse field of intent rather than skeletons. Visitors discover the rule by testing it: a raised arm produces a slow structural shift, a crowd produces a standing wave. The piece rests to near-stillness when the room empties.",
      designDirection:
        "One material, one light source, no colour except heat. At twelve metres, contrast does the work — the sculpture reads as mass and shadow first, detail second, so it stays legible from across a hall and rewards standing close.",
      technology: [
        "Depth-camera tracking pipeline with presence-field smoothing",
        "Real-time 3D choreography rendered to an LED volume",
        "Physically based fracture and reassembly simulation",
        "Show-control layer with idle, active and rest states",
      ],
      process: [
        "Scale studies — testing gesture legibility at twelve metres",
        "Tracking calibration under stage lighting conditions",
        "Choreography passes for lean, fracture and reassembly",
        "Failure planning: idle loop, degraded tracking and safe-state content",
      ],
      outcome:
        "An installation that reads as sculpture rather than screen. Because the interaction rule is singular and slow, visitors treat it as an object with temperament — and the technical stack disappears completely behind that impression.",
    },
    deliverables: [
      "Interactive sculpture",
      "Motion-tracking pipeline",
      "LED volume content",
      "Experiential design direction",
    ],
  },
  {
    slug: "orion-synapse",
    title: "ORION SYNAPSE",
    tag: "Data Art Installation",
    capability: "Interactive Solutions",
    capabilitySlug: "interactive-solutions",
    oneLiner: "Invisible infrastructure rendered as a breathing neural constellation.",
    status: STATUS.CASE_STUDY,
    liveUrl: null,
    year: "2024",
    image:
      "https://images.unsplash.com/photo-1657632843433-e6a8b7451ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMDNEJTIwZmx1aWQlMjBkYXJrJTIwbmVvbiUyMGx1eHVyeSUyMGFyY2hpdGVjdHVyZSUyMGdlb21ldHJpY3xlbnwwfHx8fDE3ODg0MjAwMTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "ORION SYNAPSE — data art installation and visual experience design by Elentara",
    seoTitle: "ORION SYNAPSE — Data Art Installation | Elentara Case Study",
    seoDescription:
      "ORION SYNAPSE by Elentara: a data art installation rendering live network traffic as a neural constellation — visual experience design powered by real data.",
    role: "Data Art, Visual Experience Design",
    intro:
      "A data art installation that renders live network activity as a breathing neural constellation.",
    sections: {
      challenge:
        "Data visualisation defaults to charts because charts are safe. But a public atrium is not a dashboard — nobody stands still to read an axis. The piece had to be immediately legible as beauty and only gradually legible as information.",
      idea:
        "Bind the data to physics instead of geometry. Volume becomes density, latency becomes drift, and bursts become pulses that travel along the constellation. Nothing is labelled; the structure teaches itself through repetition.",
      experience:
        "Standing in front of the piece, visitors first read weather, then rhythm, then meaning. Because the source is live and unrepeatable, regulars notice the difference between a quiet morning and a peak hour without ever being told what they are looking at.",
      designDirection:
        "Deep field, thin light, no chrome. The composition holds an enormous amount of dark so that a single bright pulse carries real weight. Generative typography is used sparingly, as texture and timestamp, never as a caption.",
      technology: [
        "Streaming ingest pipeline with windowed aggregation",
        "GPU particle systems with instanced link rendering",
        "Generative typographic layer for temporal markers",
        "Long-running stability harness for unattended operation",
      ],
      process: [
        "Data audit — deciding what is honest to show and what is noise",
        "Mapping studies from metric to physical behaviour",
        "Endurance testing for continuous multi-month playback",
        "Graceful degradation when the feed stalls or drops",
      ],
      outcome:
        "The mapping architecture became a reference for our later data work: physical behaviour first, encoding second. It is the clearest demonstration that an information system can be composed rather than merely plotted.",
    },
    deliverables: [
      "Data art installation",
      "Streaming data pipelines",
      "GPU particle systems",
      "Visual experience design",
    ],
  },
  {
    slug: "vantablack-atrium",
    title: "VANTABLACK ATRIUM",
    tag: "Dark-Room Browser Gallery",
    capability: "Digital Experiences",
    capabilitySlug: "digital-experiences",
    oneLiner: "A digital gallery where jewelry is discovered rather than browsed.",
    status: STATUS.CONCEPT,
    liveUrl: null,
    year: "Concept",
    image:
      "https://static.prod-images.emergentagent.com/jobs/5936bd48-65ed-4320-8e36-e81eb45e928e/images/6f201568e5b162e739074e730a71f03dad1629426c7fa6a8c29eda172eee1d8f.jpeg",
    alt: "VANTABLACK ATRIUM concept — a jewellery piece suspended in an isolated pool of light inside a dark browser gallery",
    seoTitle: "VANTABLACK ATRIUM — Dark-Room Browser Gallery Concept | Elentara",
    seoDescription:
      "VANTABLACK ATRIUM: a self-initiated Elentara concept — a flagship jewelry collection presented as a walkable dark-room browser gallery where pieces reveal themselves on approach.",
    role: "Self-initiated concept — Digital Experiences",
    intro:
      "A flagship jewelry collection presented as a walkable dark-room browser gallery. Individual pieces float inside isolated light pools and reveal themselves as the visitor approaches.",
    sections: {
      challenge:
        "Luxury e-commerce shows everything at once: a grid, a hover zoom, a price. Abundance is the opposite of desire. The question this concept asks is whether a collection can be presented the way a gallery presents work — one object, one light, one moment of arrival.",
      idea:
        "Build a room instead of a page. The visitor moves through near-total darkness where each piece exists inside its own pool of light. Nothing is visible until you are close enough to deserve it, so browsing becomes approach.",
      experience:
        "Cursor proximity drives the reveal on desktop: light blooms, the piece rotates a few degrees toward you, and material detail resolves as you close distance. On mobile the gyroscope takes over — tilting the device sweeps the light across the room, so discovery becomes physical.",
      designDirection:
        "Vantablack negative space occupying most of the frame, one cold key light per object, and type reduced to a mono caption that fades in behind the piece. No panels, no chrome, no gradients — the only decoration is the reflection on the object itself.",
      technology: [
        "Three.js scene with per-object light pools and shadow catchers",
        "Cursor-proximity and DeviceOrientation-driven reveal system",
        "Progressive asset streaming — one piece loaded at a time",
        "Reduced-motion mode presenting a still, lit composition per piece",
      ],
      process: [
        "Spatial layout studies — how far apart objects must sit to feel discovered",
        "Reveal-curve tuning so approach feels rewarded, not triggered",
        "Gyroscope mapping and touch fallbacks for mobile",
        "Performance tiering so the darkness never costs frame rate",
      ],
      outcome:
        "A self-initiated study in restraint: proof that removing almost everything from a commerce interface can increase the perceived value of what remains. Presented as a concept and not produced for a client.",
    },
    deliverables: [
      "Spatial gallery concept",
      "Proximity reveal system",
      "Gyroscope interaction model",
      "Art direction and lighting design",
    ],
  },
  {
    slug: "kepler-loom",
    title: "KEPLER LOOM",
    tag: "Environmental Generative Typeface",
    capability: "Creative Technology",
    capabilitySlug: "creative-technology",
    oneLiner: "What if a typeface could feel the atmosphere around it?",
    status: STATUS.CONCEPT,
    liveUrl: null,
    year: "Concept",
    image:
      "https://static.prod-images.emergentagent.com/jobs/5936bd48-65ed-4320-8e36-e81eb45e928e/images/1851a034d920fddecc8a4cdd07e8a0e97e1c5dd2c565b49cf18aa8fcc4a35a82.jpeg",
    alt: "KEPLER LOOM concept — a generative letterform dissolving into wind-driven filaments",
    seoTitle: "KEPLER LOOM — Environmental Generative Typeface Concept | Elentara",
    seoDescription:
      "KEPLER LOOM: a self-initiated Elentara concept — a generative typeface that redraws itself from real-time weather data, with wind, humidity and temperature shaping the letterforms.",
    role: "Self-initiated concept — Creative Technology",
    intro:
      "A generative typeface that redraws itself using real-time environmental data — atmosphere as a type designer.",
    sections: {
      challenge:
        "Variable fonts expose axes that almost nobody moves. The axes exist, but the input is missing. This concept asks what happens when the input is not a designer's slider but the sky outside the visitor's window.",
      idea:
        "Bind each typographic axis to an atmospheric measurement. Wind deforms the skeleton, humidity thickens density, temperature drives weight, and storm activity raises visual intensity — so the same headline in two cities is genuinely two different typefaces.",
      experience:
        "A visitor lands and the wordmark is already wrong for someone else. Letterforms drift and settle over a long cycle rather than animating on a loop, so returning later shows real change. A control strip lets you scrub between conditions to understand the system.",
      designDirection:
        "Off-white filaments on pure black, no colour except a faint cyan under storm conditions. The specimen layout is editorial and technical — measurement labels sit in mono at the margin like instrument readouts, never decorating the type itself.",
      technology: [
        "Procedural skeleton-to-outline generation on canvas",
        "Weather API ingest with long-period interpolation between readings",
        "Variable-axis mapping layer (wind, humidity, temperature, intensity)",
        "Static specimen fallback when data or motion is unavailable",
      ],
      process: [
        "Skeleton studies — which letter structures survive heavy deformation",
        "Axis mapping so extreme weather stays legible",
        "Interpolation tuning to avoid visible snapping between readings",
        "Specimen art direction for the editorial presentation",
      ],
      outcome:
        "A concept that reframes typography as an instrument rather than an asset. Self-initiated research, not commissioned work — and the mapping layer is directly reusable for any live-data-driven identity system.",
    },
    deliverables: [
      "Generative type system",
      "Environmental data mapping",
      "Interactive specimen",
      "Technical art direction",
    ],
  },
  {
    slug: "halcyon-bloom",
    title: "HALCYON BLOOM",
    tag: "Product Film Without The Product",
    capability: "3D Design & Motion",
    capabilitySlug: "3d-design-motion",
    oneLiner: "The product is never filmed. It is formed.",
    status: STATUS.CONCEPT,
    liveUrl: null,
    year: "Concept",
    image:
      "https://static.prod-images.emergentagent.com/jobs/5936bd48-65ed-4320-8e36-e81eb45e928e/images/cd603dd34ff8c091a199b36561c320dc722ad78cdc8a62b046f3dbecab25d988.jpeg",
    alt: "HALCYON BLOOM concept — petals, minerals and fluid forming the silhouette of a bottle that is never shown",
    seoTitle: "HALCYON BLOOM — Cinematic 3D Product Film Concept | Elentara",
    seoDescription:
      "HALCYON BLOOM: a self-initiated Elentara concept — a fragrance and skincare product film in which the product is never filmed, only formed from petals, minerals and fluid simulation.",
    role: "Self-initiated concept — 3D Design & Motion",
    intro:
      "A product film in which the actual product is never filmed. Petals, minerals, particles and fluids gradually form the silhouette of a fragrance or skincare bottle.",
    sections: {
      challenge:
        "Beauty film has one dominant grammar: hero bottle, rotating plinth, liquid splash. It is beautifully made and completely interchangeable. The concept starts from a constraint instead — the product may not appear on camera at all.",
      idea:
        "Let the ingredients build the silhouette. Petals, mineral shards and viscous fluid converge in mid-air until negative space resolves into the shape of the bottle, then disperse before the form fully closes. The audience completes the object.",
      experience:
        "Delivered as a scroll-linked film rather than a video embed: scroll position drives convergence, so the visitor controls how close the form gets to resolving. Stopping mid-scroll holds an unresolved sculpture. The silhouette only completes at the end.",
      designDirection:
        "Obsidian set, single soft key light, gold and violet accents pulled only from the materials themselves. Macro depth of field keeps one plane sharp so the eye is guided rather than overwhelmed. No text until the film resolves.",
      technology: [
        "Simulation-driven convergence — fluid, particle and rigid-body passes",
        "Scroll-linked frame sequencing with progressive image loading",
        "Physically based shading for petal translucency and mineral subsurface",
        "Reduced-motion mode delivering three composed key frames",
      ],
      process: [
        "Silhouette studies — how little is needed before the shape reads",
        "Simulation art direction across petal, mineral and fluid layers",
        "Scroll-to-time mapping and scrub-back behaviour",
        "Weight and bandwidth tiering for mobile playback",
      ],
      outcome:
        "A self-initiated demonstration that restraint can outperform reveal in product storytelling. No client, no product, no brand attached — the concept exists to prove the grammar.",
    },
    deliverables: [
      "Cinematic 3D concept film",
      "Fluid and particle simulation",
      "Scroll-linked sequencing",
      "Lighting and material direction",
    ],
  },
  {
    slug: "meridian-glass",
    title: "MERIDIAN GLASS",
    tag: "Browser-Based Architectural AR",
    capability: "Innovation Labs",
    capabilitySlug: "innovation-labs",
    oneLiner: "What if the finished building could exist before the building does?",
    status: STATUS.CONCEPT,
    liveUrl: null,
    year: "Concept",
    image:
      "https://static.prod-images.emergentagent.com/jobs/5936bd48-65ed-4320-8e36-e81eb45e928e/images/750a61cfa390da2c38abd3ac3ffc0a177b84caab26d815b349220560d9770121.jpeg",
    alt: "MERIDIAN GLASS concept — a translucent wireframe building volume overlaid on an unfinished construction site",
    seoTitle: "MERIDIAN GLASS — WebAR Architectural Visualization Concept | Elentara",
    seoDescription:
      "MERIDIAN GLASS: a self-initiated Elentara concept — browser-based AR architectural visualization that places a proposed building or interior into the real, unfinished site.",
    role: "Self-initiated concept — Innovation Labs",
    intro:
      "A browser-based AR architectural visualization experience. Point a phone at an unfinished environment and see the proposed building or interior placed into the real world.",
    sections: {
      challenge:
        "Architectural sign-off happens through renders and drawings, which ask everyone in the room to imagine the same thing from a fixed viewpoint. Site visits are the opposite: real light, real scale, nothing to look at. The concept tries to put the render on the site.",
      idea:
        "Skip the app. A browser-based AR experience that anchors the proposed volume to the ground plane and lets people walk it, with material variants switchable in real time so decisions can be made standing in the space rather than around a table.",
      experience:
        "On arrival the phone resolves the ground plane and the volume rises into place at true scale. Walking changes parallax honestly, so proportion is felt rather than argued. A single material strip allows facade, glazing and interior finishes to be swapped instantly for comparison.",
      designDirection:
        "Translucent glass volumes with thin cyan structural edges so the proposal never pretends to be photography — it reads as an overlay, which keeps it honest. All UI is pushed to a single bottom strip so the site stays visible.",
      technology: [
        "WebXR / WebAR with plane detection and world anchoring",
        "Real-time material switching on shared geometry",
        "Aggressive geometry decimation for mobile GPU budgets",
        "Non-AR fallback: an orbit viewer with the same material controls",
      ],
      process: [
        "Anchoring reliability tests across surfaces and lighting conditions",
        "Scale verification against known site dimensions",
        "Mobile thermal and battery budgeting for extended sessions",
        "Fallback flow design for unsupported devices",
      ],
      outcome:
        "A self-initiated exploration of AR as a decision tool rather than a marketing gimmick. Not produced for a client or a real development — the site imagery is conceptual.",
    },
    deliverables: [
      "WebAR experience concept",
      "Spatial tracking model",
      "Real-time material system",
      "Mobile-first interaction design",
    ],
  },
  {
    slug: "signal-choir",
    title: "SIGNAL CHOIR",
    tag: "Living Data-Art System",
    capability: "Interactive Solutions",
    capabilitySlug: "interactive-solutions",
    oneLiner: "A dataset became an instrument.",
    status: STATUS.CONCEPT,
    liveUrl: null,
    year: "Concept",
    image:
      "https://static.prod-images.emergentagent.com/jobs/5936bd48-65ed-4320-8e36-e81eb45e928e/images/8c52df6b1ea3a44510799016cca875c71ac53e13733cbf1caf8012bfd28ad1fe.jpeg",
    alt: "SIGNAL CHOIR concept — tall glowing vertical light columns of varying height rising from a dark reflective plane",
    seoTitle: "SIGNAL CHOIR — Living Data-Art System Concept | Elentara",
    seoDescription:
      "SIGNAL CHOIR: a self-initiated Elentara concept — a living data-art system that turns real-time datasets into glowing vertical forms with height, brightness, motion and pitch.",
    role: "Self-initiated concept — Interactive Solutions",
    intro:
      "A living data-art system that turns real-time datasets into glowing vertical forms — and then lets you hear them.",
    sections: {
      challenge:
        "Most live dashboards are read once and ignored. The information is present but it has no presence. This concept asks whether a dataset can be given a body and a voice without becoming dishonest about what it measures.",
      idea:
        "Give every data point a vertical form and four honest mappings: magnitude to height, intensity to brightness, rate of change to motion, and value to pitch. Because the mappings are strict and consistent, the piece can be learned — and once learned, heard.",
      experience:
        "The visitor faces a field of light columns that shift as the feed updates. Hovering isolates a column and brings its tone forward; the whole field can be played as a chord. Anomalies announce themselves as both a spike and a dissonance, which is faster than reading any chart.",
      designDirection:
        "A vast dark plane, cold cyan and violet emission, atmospheric haze for depth. Nothing is drawn that is not data — no axes, no gridlines, no decorative particles. Type appears only as a mono readout when a column is isolated.",
      technology: [
        "Streaming dataset ingest with normalisation and outlier handling",
        "Instanced GPU rendering for thousands of concurrent forms",
        "Web Audio synthesis mapped from value to pitch",
        "Muted, motion-reduced mode with the same visual encoding",
      ],
      process: [
        "Mapping honesty review — ensuring encodings cannot mislead",
        "Perceptual tuning for brightness and height ranges",
        "Sonification study to keep harmony legible at scale",
        "Accessibility pass: audio optional, encoding never audio-only",
      ],
      outcome:
        "A self-initiated study in giving data presence. Conceptual, with no client dataset attached — the architecture is designed to accept a real feed without changing the visual grammar.",
    },
    deliverables: [
      "Data-art system concept",
      "Sonification engine",
      "Instanced rendering architecture",
      "Interaction and accessibility model",
    ],
  },
];

export const getProject = (slug) => PROJECTS.find((p) => p.slug === slug);

export const getProjectNeighbours = (slug) => {
  const i = PROJECTS.findIndex((p) => p.slug === slug);
  if (i === -1) return { prev: null, next: null };
  return {
    prev: PROJECTS[(i - 1 + PROJECTS.length) % PROJECTS.length],
    next: PROJECTS[(i + 1) % PROJECTS.length],
  };
};
